import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { HeaderNav } from "@/components/HeaderNav";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DeletionRequests, BlockedAlertWarning } from "@/components/admin/DeletionRequests";
import { GlimpseStats } from "@/components/admin/GlimpseStats";
import { AssessmentDropoff } from "@/components/admin/AssessmentDropoff";
import { Building2, ListChecks, Layers, SlidersHorizontal, Sparkles, MessageSquare, Search, ScrollText, Database, Activity, Ruler, FlaskConical, FileText, Palette, Upload, CreditCard, Gift, Copy, Ticket, Mail, UserX, Eye, TrendingDown } from "lucide-react";

const TABS = [
  { key: "usage", label: "Overview", icon: Activity },
  { key: "orgs", label: "Organizations", icon: Building2 },
  { key: "items", label: "Item Bank", icon: ListChecks },
  { key: "dimensions", label: "Dimensions", icon: Layers },
  { key: "scoring", label: "Scoring", icon: SlidersHorizontal },
  { key: "norms", label: "Norms", icon: Ruler },
  { key: "archetypes", label: "Archetypes", icon: Sparkles },
  { key: "prompts", label: "AI Prompts", icon: MessageSquare },
  { key: "inspect", label: "Scoring Inspector", icon: Search },
  { key: "research", label: "Research Export", icon: Database },
  { key: "pilot", label: "Assessment Insights", icon: FlaskConical },
  { key: "glimpse", label: "Glimpse Stats", icon: Eye },
  { key: "dropoff", label: "Drop-off", icon: TrendingDown },
  { key: "payments", label: "Purchases", icon: CreditCard },
  { key: "promos", label: "Promo Codes", icon: Ticket },
  { key: "followup", label: "Follow-Up Emails", icon: Mail },
  { key: "welcome", label: "Welcome Pages", icon: ScrollText },
  { key: "content", label: "Participant Copy", icon: FileText },
  { key: "brand", label: "Brand Settings", icon: Palette },
  { key: "deletions", label: "Deletion Requests", icon: UserX },
  { key: "audit", label: "Audit Log", icon: ScrollText },
];

const Field = ({ label, children }) => (
  <label className="block"><span className="cg-eyebrow">{label}</span><div className="mt-1.5">{children}</div></label>
);
const input = "w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2.5 text-sm text-[#F3F4F6] focus:border-brass outline-none";
const btn = "bg-brass text-obsidian text-sm font-medium px-4 py-2 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-40";
const btnGhost = "border border-[var(--cg-border)] text-sm px-4 py-2 rounded-full hover:border-brass cg-scale-btn text-[#9CA3AF]";

export default function AdminPanel() {
  const [tab, setTab] = useState("usage");
  return (
    <div>
      <HeaderNav />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 pb-24">
        <p className="cg-eyebrow mb-2">Product Owner · Super Admin</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#F3F4F6] mb-8">Configuration</h1>
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm cg-scale-btn border ${tab === t.key ? "border-brass bg-[rgba(217,162,95,0.1)] text-[#F3F4F6]" : "border-[var(--cg-border)] text-[#9CA3AF] hover:border-brass/40"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
        <div className="cg-fade">
          {tab === "usage" && <Usage onOpenDeletions={() => setTab("deletions")} />}
          {tab === "orgs" && <Orgs />}
          {tab === "items" && <Items />}
          {tab === "dimensions" && <Dimensions />}
          {tab === "scoring" && <Scoring />}
          {tab === "norms" && <Norms />}
          {tab === "archetypes" && <Archetypes />}
          {tab === "prompts" && <Prompts />}
          {tab === "inspect" && <Inspector />}
          {tab === "research" && <Research />}
          {tab === "pilot" && <Pilot />}
          {tab === "glimpse" && <GlimpseStats />}
          {tab === "dropoff" && <AssessmentDropoff />}
          {tab === "payments" && <Payments />}
          {tab === "promos" && <Promos />}
          {tab === "followup" && <FollowupEmails />}
          {tab === "welcome" && <WelcomePages />}
          {tab === "content" && <ContentEditor />}
          {tab === "brand" && <BrandSettings />}
          {tab === "deletions" && <DeletionRequests />}
          {tab === "audit" && <Audit />}
        </div>
      </div>
    </div>
  );
}

function Card({ children, testId }) { return <div className="cg-card p-6" data-testid={testId}>{children}</div>; }

function Usage({ onOpenDeletions }) {
  const [u, setU] = useState(null);
  const [openDel, setOpenDel] = useState(null);
  const [blocked, setBlocked] = useState([]);
  useEffect(() => {
    api.get("/admin/usage").then((r) => setU(r.data));
    api.get("/admin/deletion-requests").then((r) => { setOpenDel(r.data.open_count); setBlocked(r.data.blocked_alerts || []); }).catch(() => {});
  }, []);
  if (!u) return <p className="cg-eyebrow animate-pulse">Loading…</p>;
  const items = [["Organizations", u.organizations], ["Campaigns", u.campaigns], ["Participants", u.participants], ["Completed", u.completed_assessments], ["AI interpretations", u.ai_interpretations], ["AI failures", u.ai_failures]];
  return (
    <div className="space-y-4">
      <BlockedAlertWarning alerts={blocked} testId="overview-blocked-alert-warning" />
      {openDel > 0 && (
        <button data-testid="overview-open-deletions-badge" onClick={onOpenDeletions}
          className="w-full text-left cg-card p-4 border-terracotta/60 flex items-center justify-between hover:border-terracotta transition-colors">
          <span className="text-sm text-[#F3F4F6]"><span className="font-sans font-semibold tabular-nums text-xl text-terracotta mr-2">{openDel}</span>open account deletion {openDel === 1 ? "request" : "requests"}</span>
          <span className="text-xs text-[#9CA3AF]">Review →</span>
        </button>
      )}
      <div className="grid sm:grid-cols-3 gap-4" data-testid="usage-grid">
        {items.map(([l, v]) => (<Card key={l}><p className="cg-eyebrow mb-1">{l}</p><p className="font-serif text-4xl text-[#F3F4F6]">{v}</p></Card>))}
      </div>
    </div>
  );
}

function Orgs() {
  const [orgs, setOrgs] = useState([]);
  const [sortBy, setSortBy] = useState("name");
  const [form, setForm] = useState({ organization_name: "", organization_code: "", min_reporting_threshold: 7 });
  const [admin, setAdmin] = useState({ name: "", email: "", password: "", organization_id: "" });
  const load = useCallback(() => api.get("/admin/organizations").then((r) => setOrgs(r.data.organizations)), []);
  const [delOrg, setDelOrg] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const doDeleteOrg = async () => {
    setDelBusy(true);
    try { await api.delete(`/admin/organizations/${delOrg.id}`); toast.success(`Deleted ${delOrg.organization_name}`); setDelOrg(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Organization could not be deleted."); }
    finally { setDelBusy(false); }
  };
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try { await api.post("/admin/organizations", form); toast.success("Organization created"); setForm({ organization_name: "", organization_code: "", min_reporting_threshold: 7 }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createAdmin = async () => {
    try { await api.post("/admin/org-admins", admin); toast.success("Org admin created"); setAdmin({ name: "", email: "", password: "", organization_id: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <Card testId="create-org-card">
          <h3 className="font-serif text-xl text-[#F3F4F6] mb-4">Create organization (tenant)</h3>
          <div className="space-y-3">
            <Field label="Name"><input data-testid="org-name-input" className={input} value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} /></Field>
            <Field label="Code"><input data-testid="org-code-input" className={input} value={form.organization_code} onChange={(e) => setForm({ ...form, organization_code: e.target.value })} placeholder="e.g. ACME" /></Field>
            <Field label="Min reporting threshold"><input data-testid="org-threshold-input" type="number" className={input} value={form.min_reporting_threshold} onChange={(e) => setForm({ ...form, min_reporting_threshold: Number(e.target.value) })} /></Field>
            <button data-testid="org-create-button" className={btn} onClick={create} disabled={!form.organization_name || !form.organization_code}>Create</button>
          </div>
        </Card>
        <Card testId="create-orgadmin-card">
          <h3 className="font-serif text-xl text-[#F3F4F6] mb-4">Create organization administrator</h3>
          <div className="space-y-3">
            <Field label="Organization"><select data-testid="orgadmin-org-select" className={input} value={admin.organization_id} onChange={(e) => setAdmin({ ...admin, organization_id: e.target.value })}><option value="">Select…</option>{orgs.map((o) => <option key={o.id} value={o.id}>{o.organization_name}</option>)}</select></Field>
            <Field label="Name"><input data-testid="orgadmin-name-input" className={input} value={admin.name} onChange={(e) => setAdmin({ ...admin, name: e.target.value })} /></Field>
            <Field label="Email"><input data-testid="orgadmin-email-input" className={input} value={admin.email} onChange={(e) => setAdmin({ ...admin, email: e.target.value })} /></Field>
            <Field label="Password"><input data-testid="orgadmin-password-input" className={input} value={admin.password} onChange={(e) => setAdmin({ ...admin, password: e.target.value })} /></Field>
            <button data-testid="orgadmin-create-button" className={btn} onClick={createAdmin} disabled={!admin.organization_id || !admin.email || admin.password.length < 8}>Create admin</button>
          </div>
        </Card>
      </div>
      <Card testId="orgs-list">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-serif text-xl text-[#F3F4F6]">Organizations</h3>
          <select data-testid="org-sort-select" className={`${input} max-w-[240px]`} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="name">Sort: Name (A→Z)</option>
            <option value="completed">Sort: Completed (high→low)</option>
            <option value="eligible_campaigns">Sort: Eligible campaigns (high→low)</option>
            <option value="last_activity">Sort: Last activity (newest)</option>
          </select>
        </div>
        <div className="space-y-2" data-testid="org-sorted-list">
          {[...orgs].sort((x, y) => {
            if (sortBy === "name") return x.organization_name.localeCompare(y.organization_name);
            if (sortBy === "last_activity") return (y.last_activity || "").localeCompare(x.last_activity || "");
            return (y[sortBy] || 0) - (x[sortBy] || 0);
          }).map((o) => (
            <div key={o.id} className="flex items-center justify-between border-b border-[var(--cg-border)] py-3 last:border-0">
              <div><p className="text-[#F3F4F6]">{o.organization_name} <span className="font-mono text-xs text-brass">{o.organization_code}</span> {o.is_demo && <span className="cg-eyebrow"> demo</span>}
                {o.eligible
                  ? <span data-testid={`org-elig-${o.id}`} className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[rgba(125,157,134,0.2)] text-[#7D9D86]">eligible · {o.completed}</span>
                  : <span data-testid={`org-elig-${o.id}`} className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#222634] text-[#6B7280]">{o.completed}/{o.min_reporting_threshold}</span>}</p>
                <p className="text-xs text-[#6B7280] font-mono">{o.campaigns} campaigns · {o.admins} admins · threshold {o.min_reporting_threshold}</p>
                <p className="text-xs text-[#6B7280] font-mono mt-0.5" data-testid={`org-health-${o.id}`}>{o.completed} completed · {o.eligible_campaigns}/{o.campaigns} eligible campaigns · last activity {o.last_activity ? o.last_activity.slice(0, 10) : "—"}
                {(() => { const t = o.completed_this_week || 0, p = o.completed_prev_week || 0; const up = t > p, flat = t === p; return <span data-testid={`org-trend-${o.id}`} className={up ? "text-[#7D9D86]" : flat ? "text-[#6B7280]" : "text-terracotta"}> · {up ? "▲" : flat ? "▬" : "▼"} {t} this wk vs {p} last</span>; })()}</p></div>
              <div className="flex items-center gap-2 shrink-0">
                <Link data-testid={`view-org-portrait-${o.id}`} to={`/admin/org/${o.id}`} className={btnGhost}>View portrait →</Link>
                <button data-testid={`delete-org-${o.id}`} onClick={() => setDelOrg(o)} className="text-xs px-3 py-1.5 rounded-full bg-[rgba(196,98,67,0.15)] text-terracotta hover:bg-[rgba(196,98,67,0.3)] cg-scale-btn">Delete Organization</button>
              </div>
            </div>
          ))}
        </div>
      </Card>
      {delOrg && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="delete-org-modal" onClick={() => !delBusy && setDelOrg(null)}>
          <div className="cg-card p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-[#F3F4F6] mb-3">Delete Organization?</h3>
            <p className="text-sm text-[#9CA3AF] mb-5">Are you sure you want to permanently delete <span className="text-[#F3F4F6]">{delOrg.organization_name}</span> and all of its associated organizational data?</p>
            <div className="flex gap-2 justify-end">
              <button className="text-sm text-[#9CA3AF] px-4 py-2 rounded-full border border-[var(--cg-border)]" onClick={() => setDelOrg(null)} disabled={delBusy}>Cancel</button>
              <button data-testid="confirm-delete-org-button" className="text-sm bg-terracotta text-white px-5 py-2 rounded-full disabled:opacity-40" onClick={doDeleteOrg} disabled={delBusy}>Yes, Delete Organization</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SCALE_LABELS = ["Strongly disagree", "Disagree", "Neither agree nor disagree", "Agree", "Strongly agree"];

// Word-level diff (LCS) for comparing live vs. draft item wording.
function diffWords(a, b) {
  const aw = (a || "").split(/(\s+)/), bw = (b || "").split(/(\s+)/);
  const n = aw.length, m = bw.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--)
    dp[i][j] = aw[i] === bw[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const res = []; let i = 0, j = 0;
  while (i < n && j < m) {
    if (aw[i] === bw[j]) { res.push({ t: aw[i], k: "same" }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { res.push({ t: aw[i], k: "del" }); i++; }
    else { res.push({ t: bw[j], k: "add" }); j++; }
  }
  while (i < n) { res.push({ t: aw[i], k: "del" }); i++; }
  while (j < m) { res.push({ t: bw[j], k: "add" }); j++; }
  return res;
}

const DiffText = ({ oldText, newText, testId }) => (
  <p className="text-sm leading-relaxed" data-testid={testId}>
    {diffWords(oldText, newText).map((s, i) => (
      <span key={i} className={s.k === "del" ? "text-terracotta line-through" : s.k === "add" ? "text-[#7D9D86]" : "text-[#E8EAEE]"}>{s.t}</span>
    ))}
  </p>
);

function Items() {
  const [items, setItems] = useState([]);
  const [dims, setDims] = useState([]);
  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState("");
  const [filter, setFilter] = useState("all");
  const [draft, setDraft] = useState({ item_text: "", dimension: "", territory: "", reverse_scored: false, weight: 1.0 });
  const [editing, setEditing] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [showServed, setShowServed] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadVersions = useCallback(async () => {
    const r = await api.get("/admin/versions");
    setVersions(r.data.versions);
    const act = r.data.versions.find((v) => v.active);
    setVersion((cur) => cur || act?.version || r.data.versions[0]?.version || "");
  }, []);
  const loadItems = useCallback((ver) => { if (ver) api.get(`/admin/items?version=${ver}`).then((r) => setItems(r.data.items)); }, []);
  useEffect(() => { loadVersions(); api.get("/admin/dimensions").then((r) => setDims(r.data.dimensions)); }, [loadVersions]);
  useEffect(() => { loadItems(version); }, [version, loadItems]);
  useEffect(() => { setIntegrity(null); setShowServed(false); if (version) api.get(`/admin/versions/${version}/integrity`).then((r) => setIntegrity(r.data)).catch(() => {}); }, [version]);

  const curVersion = versions.find((v) => v.version === version);
  const editable = curVersion && !curVersion.published;
  const wordingEditable = curVersion && curVersion.published && curVersion.active;

  const toggle = async (it) => { try { await api.patch(`/admin/items/${it.id}`, { active: !it.active }); loadItems(version); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const addItem = async () => {
    try { await api.post("/admin/items", { ...draft, weight: Number(draft.weight), assessment_version: version }); toast.success("Item added"); setDraft({ item_text: "", dimension: "", territory: "", reverse_scored: false, weight: 1.0 }); loadItems(version); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    const payload = editable
      ? { item_text: editing.item_text, dimension: editing.dimension, territory: editing.territory, reverse_scored: editing.reverse_scored, weight: Number(editing.weight) }
      : { item_text: editing.item_text, territory: editing.territory };
    try { await api.patch(`/admin/items/${editing.id}`, payload); toast.success("Item wording updated — live in new sessions"); setEditing(null); loadItems(version); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (it) => { try { await api.delete(`/admin/items/${it.id}`); toast.success("Item deleted"); loadItems(version); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const openHistory = async (it) => {
    if (historyFor === it.id) { setHistoryFor(null); return; }
    setHistoryFor(it.id); setHistory([]); setHistoryLoading(true);
    try { const r = await api.get(`/admin/items/${it.id}/history`); setHistory(r.data.history); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setHistoryLoading(false); }
  };

  const downloadAnalysis = async () => {
    const token = localStorage.getItem("cg_token");
    const r = await fetch(`${API}/admin/item-analysis.csv?version=${version}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Could not export item analysis"); return; }
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `item-analysis-${version}.csv`; a.click(); URL.revokeObjectURL(url);
    toast.success("Item analysis downloaded");
  };

  const shown = filter === "all" ? items : items.filter((i) => i.dimension === filter);

  return (
    <Card testId="items-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-serif text-xl text-[#F3F4F6]">Item bank · {items.length} items</h3>
        <div className="flex gap-2">
          <select data-testid="item-version-select" className={`${input} max-w-[220px]`} value={version} onChange={(e) => { setEditing(null); setVersion(e.target.value); }}>
            {versions.map((v) => <option key={v.version} value={v.version}>{v.display_name || v.version}{v.active ? " (active)" : ""}{v.published ? "" : " · draft"}</option>)}
          </select>
          <select data-testid="item-filter-select" className={`${input} max-w-[200px]`} value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All dimensions</option>
            {dims.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
          </select>
          <button className={btnGhost} data-testid="item-analysis-download" onClick={downloadAnalysis}>Item analysis CSV</button>
        </div>
      </div>

      {integrity && (
        <div className={`rounded-xl p-4 mb-5 border ${integrity.valid ? "border-[#3d5a45] bg-[rgba(125,157,134,0.08)]" : "border-terracotta/50 bg-[rgba(198,93,59,0.08)]"}`} data-testid="integrity-panel">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <p className="cg-eyebrow">Assessment integrity · {integrity.version}</p>
            <span data-testid="integrity-valid" className={`text-xs font-mono px-2.5 py-1 rounded-full ${integrity.valid ? "bg-[#7D9D86] text-obsidian" : "bg-terracotta text-white"}`}>
              {integrity.valid ? "VALID FOR PUBLICATION" : "INVALID — DO NOT PUBLISH"}
            </span>
          </div>
          <p className="text-sm text-[#E8EAEE] mb-2" data-testid="integrity-total">Total active items: <span className={integrity.total === integrity.expected_total ? "text-[#F3F4F6]" : "text-terracotta"}>{integrity.total}/{integrity.expected_total}</span></p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
            {integrity.per_dimension.map((p) => (
              <div key={p.dimension} className="flex justify-between text-sm">
                <span className="text-[#9CA3AF]">{p.name}</span>
                <span className={`font-mono ${p.ok ? "text-[#B9BDC7]" : "text-terracotta"}`} data-testid={`integrity-dim-${p.dimension}`}>{p.count}/{p.expected}</span>
              </div>
            ))}
          </div>
          {(integrity.duplicate_item_ids.length > 0 || integrity.missing_dimension.length > 0 || integrity.duplicate_uuids.length > 0) && (
            <p className="text-xs text-terracotta font-mono mt-3">
              {integrity.duplicate_item_ids.length > 0 && <>Duplicate item_ids: {integrity.duplicate_item_ids.join(", ")}. </>}
              {integrity.duplicate_uuids.length > 0 && <>Duplicate uuids: {integrity.duplicate_uuids.length}. </>}
              {integrity.missing_dimension.length > 0 && <>Missing dimension: {integrity.missing_dimension.join(", ")}.</>}
            </p>
          )}
          <button className={`${btnGhost} mt-3`} data-testid="integrity-inspect" onClick={() => setShowServed((s) => !s)}>
            {showServed ? "Hide" : "Inspect"} the {integrity.total} served items
          </button>
          {showServed && (
            <div className="mt-3 max-h-64 overflow-y-auto space-y-1 border-t border-[var(--cg-border)] pt-3" data-testid="integrity-served-list">
              {integrity.served_items.map((s) => (
                <p key={s.item_id} className="text-xs text-[#9CA3AF]"><span className="font-mono text-[#6B7280]">{s.item_id}</span> · {s.dimension}{s.reverse_scored ? " · rev" : ""} — {s.item_text}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {editable ? (
        <div className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-xl p-4 mb-5" data-testid="item-add-form">
          <p className="cg-eyebrow mb-3">Add item to draft {version}</p>
          <textarea data-testid="item-add-text" className={`${input} h-16 mb-3`} placeholder="Item text" value={draft.item_text} onChange={(e) => setDraft({ ...draft, item_text: e.target.value })} />
          <div className="grid sm:grid-cols-4 gap-3 items-center">
            <select data-testid="item-add-dimension" className={input} value={draft.dimension} onChange={(e) => setDraft({ ...draft, dimension: e.target.value })}>
              <option value="">Dimension…</option>{dims.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
            </select>
            <input data-testid="item-add-territory" className={input} placeholder="Territory (optional)" value={draft.territory} onChange={(e) => setDraft({ ...draft, territory: e.target.value })} />
            <input data-testid="item-add-weight" type="number" step="0.1" className={input} placeholder="Weight" value={draft.weight} onChange={(e) => setDraft({ ...draft, weight: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-[#9CA3AF]"><input type="checkbox" data-testid="item-add-reverse" checked={draft.reverse_scored} onChange={(e) => setDraft({ ...draft, reverse_scored: e.target.checked })} className="w-4 h-4 accent-[#D9A25F]" /> Reverse</label>
          </div>
          <button className={`${btn} mt-3`} data-testid="item-add-button" onClick={addItem} disabled={!draft.item_text || !draft.dimension}>Add item</button>
        </div>
      ) : wordingEditable ? (
        <p className="text-xs text-[#9CA3AF] mb-4 leading-relaxed" data-testid="wording-edit-notice">This is the live published assessment. You can edit <span className="text-brass">item wording</span> and <span className="text-brass">territory</span> inline below. Changes appear in new assessment sessions immediately and never alter historical results or scoring. To change dimensions, weights, reverse-scoring, or to add/remove items, create a new draft version.</p>
      ) : (
        <p className="text-xs text-[#6B7280] mb-4 font-mono">This version is published &amp; immutable to protect historical results. Select or create a draft version to edit items.</p>
      )}

      <div className="space-y-2 max-h-[520px] overflow-y-auto pr-2" data-testid="items-list">
        {shown.map((it) => editing?.id === it.id ? (
          <div key={it.id} className="border border-brass/40 rounded-xl p-4 bg-[#0D0E12]" data-testid={`item-edit-${it.item_id}`}>
            <p className="cg-eyebrow mb-2">{it.item_id} · editing {editable ? "(full)" : "wording & territory"}</p>
            <textarea className={`${input} h-16 mb-3`} data-testid={`item-edit-text-${it.item_id}`} value={editing.item_text} onChange={(e) => setEditing({ ...editing, item_text: e.target.value })} />
            {editable ? (
              <div className="grid sm:grid-cols-4 gap-3 items-center mb-3">
                <select className={input} value={editing.dimension} onChange={(e) => setEditing({ ...editing, dimension: e.target.value })}>{dims.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}</select>
                <input className={input} value={editing.territory || ""} onChange={(e) => setEditing({ ...editing, territory: e.target.value })} />
                <input type="number" step="0.1" className={input} value={editing.weight} onChange={(e) => setEditing({ ...editing, weight: e.target.value })} />
                <label className="flex items-center gap-2 text-sm text-[#9CA3AF]"><input type="checkbox" checked={editing.reverse_scored} onChange={(e) => setEditing({ ...editing, reverse_scored: e.target.checked })} className="w-4 h-4 accent-[#D9A25F]" /> Reverse</label>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3 items-end mb-3">
                <div>
                  <label className="cg-eyebrow block mb-1">Territory</label>
                  <input className={input} data-testid={`item-edit-territory-${it.item_id}`} value={editing.territory || ""} onChange={(e) => setEditing({ ...editing, territory: e.target.value })} />
                </div>
                <p className="text-xs text-[#6B7280] font-mono pb-2">{editing.dimension} · w{editing.weight}{editing.reverse_scored ? " · reverse" : ""} <span className="text-[#4B5563]">(scoring locked)</span></p>
              </div>
            )}
            <div className="rounded-lg border border-[var(--cg-border)] bg-[#0A0B0E] p-4 mb-3" data-testid={`item-preview-${it.item_id}`}>
              <p className="cg-eyebrow mb-2">Participant preview</p>
              <p className="text-sm text-[#E8EAEE] mb-3">{editing.item_text ? editing.item_text : <span className="text-[#6B7280]">(empty)</span>}</p>
              <div className="flex flex-wrap gap-2">
                {SCALE_LABELS.map((l) => <span key={l} className="text-[11px] px-2.5 py-1 rounded-full border border-[var(--cg-border)] text-[#9CA3AF]">{l}</span>)}
              </div>
            </div>
            <div className="rounded-lg border border-[var(--cg-border)] bg-[#0A0B0E] p-4 mb-3" data-testid={`item-compare-${it.item_id}`}>
              <p className="cg-eyebrow mb-2">Draft &amp; compare</p>
              {editing.item_text === it.item_text && (editing.territory || "") === (it.territory || "") ? (
                <p className="text-xs text-[#6B7280]">No changes yet. Edit the wording or territory above to compare with the current live text.</p>
              ) : (
                <>
                  {editing.item_text !== it.item_text && (
                    <div className="mb-3">
                      <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Wording <span className="text-terracotta">removed</span> / <span className="text-[#7D9D86]">added</span></p>
                      <DiffText oldText={it.item_text} newText={editing.item_text} testId={`item-diff-${it.item_id}`} />
                      <div className="grid sm:grid-cols-2 gap-3 mt-2">
                        <div><p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Live now</p><p className="text-xs text-[#9CA3AF] leading-relaxed">{it.item_text}</p></div>
                        <div><p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Your draft</p><p className="text-xs text-[#E8EAEE] leading-relaxed">{editing.item_text || <span className="text-[#6B7280]">(empty)</span>}</p></div>
                      </div>
                    </div>
                  )}
                  {(editing.territory || "") !== (it.territory || "") && (
                    <p className="text-xs text-[#9CA3AF]">territory: <span className="text-terracotta line-through">{it.territory || "—"}</span> → <span className="text-[#7D9D86]">{editing.territory || "—"}</span></p>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-2"><button className={btn} data-testid={`item-save-${it.item_id}`} onClick={saveEdit} disabled={!editing.item_text?.trim()}>Save</button><button className={btnGhost} onClick={() => setEditing(null)}>Cancel</button></div>
          </div>
        ) : (
          <div key={it.id}>
            <div className="flex items-start justify-between gap-4 border-b border-[var(--cg-border)] py-3 last:border-0">
              <div className="flex-1">
                <p className="text-sm text-[#E8EAEE]">{it.item_text}</p>
                <p className="text-xs text-[#6B7280] font-mono mt-1">{it.item_id} · {it.dimension} · {it.territory} {it.reverse_scored && <span className="text-terracotta">· reverse</span>} · w{it.weight}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {(editable || wordingEditable) && <button data-testid={`item-editbtn-${it.item_id}`} onClick={() => setEditing({ ...it })} className="text-xs px-3 py-1 rounded-full bg-[#222634] text-[#9CA3AF] hover:text-brass cg-scale-btn">{editable ? "edit" : "edit wording"}</button>}
                <button data-testid={`item-historybtn-${it.item_id}`} onClick={() => openHistory(it)} className={`text-xs px-3 py-1 rounded-full cg-scale-btn ${historyFor === it.id ? "bg-[rgba(217,162,95,0.15)] text-brass" : "bg-[#222634] text-[#9CA3AF] hover:text-brass"}`}>history</button>
                <button data-testid={`item-toggle-${it.item_id}`} onClick={() => toggle(it)} disabled={!editable}
                  className={`text-xs px-3 py-1 rounded-full ${it.active ? "bg-[rgba(125,157,134,0.2)] text-[#7D9D86]" : "bg-[#222634] text-[#6B7280]"} ${!editable && "opacity-50"}`}>{it.active ? "active" : "inactive"}</button>
                {editable && <button data-testid={`item-delete-${it.item_id}`} onClick={() => del(it)} className="text-xs px-3 py-1 rounded-full bg-[rgba(196,98,67,0.15)] text-terracotta hover:bg-[rgba(196,98,67,0.3)] cg-scale-btn">delete</button>}
              </div>
            </div>
            {historyFor === it.id && (
              <div className="border border-[var(--cg-border)] rounded-xl p-4 my-2 bg-[#0D0E12]" data-testid={`item-history-${it.item_id}`}>
                <p className="cg-eyebrow mb-3">Live wording edit history · {it.item_id}</p>
                {historyLoading ? (
                  <p className="text-xs text-[#9CA3AF] animate-pulse">Loading…</p>
                ) : history.length === 0 ? (
                  <p className="text-xs text-[#6B7280]">No live edits recorded yet. Edits made from this panel on the live assessment will appear here (old → new, who, when).</p>
                ) : (
                  <div className="space-y-3">
                    {history.map((h, i) => (
                      <div key={i} className="border-b border-[var(--cg-border)] pb-3 last:border-0">
                        <p className="text-[11px] text-[#6B7280] font-mono mb-1">{new Date(h.created_at).toLocaleString()} · {h.by_name || h.by_email || "—"}</p>
                        {h.old_text != null && <DiffText oldText={h.old_text} newText={h.new_text} />}
                        {h.old_territory != null && h.old_territory !== h.new_territory && (
                          <p className="text-xs text-[#9CA3AF] mt-1">territory: <span className="text-terracotta line-through">{h.old_territory || "—"}</span> → <span className="text-[#7D9D86]">{h.new_territory || "—"}</span></p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function Dimensions() {
  const [dims, setDims] = useState([]);
  useEffect(() => { api.get("/admin/dimensions").then((r) => setDims(r.data.dimensions)); }, []);
  const save = async (d) => { try { await api.patch(`/admin/dimensions/${d.key}`, { name: d.name, description: d.description, orientation: d.orientation }); toast.success("Saved"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-4" data-testid="dimensions-list">
      {dims.map((d, i) => (
        <Card key={d.key} testId={`dimension-${d.key}`}>
          <div className="flex items-center gap-3 mb-3">
            <input className={`${input} font-serif text-lg`} value={d.name} onChange={(e) => { const n = [...dims]; n[i] = { ...d, name: e.target.value }; setDims(n); }} />
            <select className={`${input} max-w-[220px]`} value={d.orientation} onChange={(e) => { const n = [...dims]; n[i] = { ...d, orientation: e.target.value }; setDims(n); }}>
              <option value="authority_retaining">authority_retaining</option>
              <option value="authority_yielding">authority_yielding</option>
            </select>
          </div>
          <textarea className={`${input} h-24`} value={d.description} onChange={(e) => { const n = [...dims]; n[i] = { ...d, description: e.target.value }; setDims(n); }} />
          <button className={`${btn} mt-3`} data-testid={`dimension-save-${d.key}`} onClick={() => save(d)}>Save definition</button>
        </Card>
      ))}
    </div>
  );
}

function Scoring() {
  const [versions, setVersions] = useState([]);
  const [active, setActive] = useState(null);
  const [config, setConfig] = useState("");
  const [ca, setCa] = useState("");
  const [cb, setCb] = useState("");
  const load = useCallback(async () => {
    const r = await api.get("/admin/versions"); setVersions(r.data.versions);
    const act = r.data.versions.find((v) => v.active); setActive(act);
    if (act) { const sc = await api.get(`/admin/scoring-config/${act.version}`); setConfig(JSON.stringify(sc.data.scoring_config?.config || {}, null, 2)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await api.patch(`/admin/scoring-config/${active.version}`, JSON.parse(config)); toast.success("Scoring config saved"); }
    catch (e) { toast.error("Invalid JSON or " + formatApiError(e.response?.data?.detail)); }
  };
  const createDraft = async () => { try { const r = await api.post("/admin/versions", {}); toast.success(`Draft ${r.data.version.version} created`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const publish = async (v) => { try { await api.post(`/admin/versions/${v}/publish`); toast.success("Published & activated"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const activate = async (v) => { try { await api.post(`/admin/versions/${v}/activate`); toast.success("Activated (rollback)"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const setCompat = async (comparable) => {
    if (!ca || !cb || ca === cb) { toast.error("Pick two different versions"); return; }
    try { await api.post("/admin/versions/compatibility", { version_a: ca, version_b: cb, comparable }); toast.success(comparable ? "Marked comparable" : "Compatibility removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-5">
      <Card testId="versions-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-[#F3F4F6]">Assessment versions</h3>
          <button className={btn} data-testid="create-draft-button" onClick={createDraft}>New draft version</button>
        </div>
        <div className="space-y-2" data-testid="versions-list">
          {versions.map((v) => (
            <div key={v.version} className="flex items-center justify-between border-b border-[var(--cg-border)] py-3 last:border-0">
              <div>
                <p className="text-sm text-[#E8EAEE]">{v.display_name || v.version} {v.active && <span className="text-brass">· active</span>} {v.published ? <span className="text-[#7D9D86]">· published</span> : <span className="text-[#6B7280]">· draft</span>}</p>
                <p className="font-mono text-xs text-[#6B7280]">{v.version}{v.is_validated === false ? " · not yet validated" : ""}{v.is_demo ? " · DEMO/TEST — not for real assessment" : ""}{v.comparable_with?.length > 0 ? ` · comparable: ${v.comparable_with.join(", ")}` : ""}</p>
              </div>
              <div className="flex gap-2">
                <Link className={btnGhost} data-testid={`preview-${v.version}`} to={`/admin/preview/${v.version}`}>Preview &amp; test</Link>
                {!v.published && <button className={btnGhost} data-testid={`publish-${v.version}`} onClick={() => publish(v.version)}>Publish</button>}
                {!v.active && <button className={btnGhost} data-testid={`activate-${v.version}`} onClick={() => activate(v.version)}>Activate</button>}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-[var(--cg-border)]" data-testid="version-compat">
          <p className="cg-eyebrow mb-2">Version compatibility · pool comparable versions in org-wide reports</p>
          <div className="flex flex-wrap gap-2 items-center">
            <select data-testid="compat-a" className={`${input} max-w-[180px]`} value={ca} onChange={(e) => setCa(e.target.value)}><option value="">Version A</option>{versions.map((v) => <option key={v.version} value={v.version}>{v.version}</option>)}</select>
            <select data-testid="compat-b" className={`${input} max-w-[180px]`} value={cb} onChange={(e) => setCb(e.target.value)}><option value="">Version B</option>{versions.map((v) => <option key={v.version} value={v.version}>{v.version}</option>)}</select>
            <button className={btnGhost} data-testid="compat-link" disabled={!ca || !cb || ca === cb} onClick={() => setCompat(true)}>Mark comparable</button>
            <button className={btnGhost} data-testid="compat-unlink" disabled={!ca || !cb || ca === cb} onClick={() => setCompat(false)}>Remove</button>
          </div>
          <p className="text-xs text-[#6B7280] mt-2 font-mono">Only mark versions comparable when their items &amp; scoring are intentionally equivalent. Historical results are never changed.</p>
        </div>
      </Card>
      <Card testId="scoring-config-card">
        <h3 className="font-serif text-xl text-[#F3F4F6] mb-1">Scoring configuration · {active?.version}</h3>
        <p className="text-xs text-[#6B7280] mb-3 font-mono">Weights, bands &amp; thresholds are DEMO placeholders — not validated cut scores.</p>
        <textarea data-testid="scoring-config-textarea" className={`${input} font-mono h-56`} value={config} onChange={(e) => setConfig(e.target.value)} />
        <button className={`${btn} mt-3`} data-testid="scoring-config-save" onClick={save}>Save config</button>
      </Card>
    </div>
  );
}

const BANDS = ["higher", "moderate", "lower"];

function RuleEditor({ rules, dims, onChange }) {
  const set = (i, patch) => { const n = rules.map((r, j) => (j === i ? { ...r, ...patch } : r)); onChange(n); };
  const toggleBand = (i, b) => { const cur = rules[i].band || []; set(i, { band: cur.includes(b) ? cur.filter((x) => x !== b) : [...cur, b] }); };
  return (
    <div className="space-y-2">
      {rules.map((r, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg p-2" data-testid={`rule-row-${i}`}>
          <select className={`${input} max-w-[220px]`} value={r.dimension || ""} onChange={(e) => set(i, { dimension: e.target.value })}>
            <option value="">Dimension…</option>{dims.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
          </select>
          <div className="flex gap-2">
            {BANDS.map((b) => (
              <label key={b} className="flex items-center gap-1 text-xs text-[#9CA3AF]">
                <input type="checkbox" checked={(r.band || []).includes(b)} onChange={() => toggleBand(i, b)} className="w-4 h-4 accent-[#D9A25F]" /> {b}
              </label>
            ))}
          </div>
          <button className="text-xs text-[#9CA3AF] hover:text-terracotta ml-auto" onClick={() => onChange(rules.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      <button className={btnGhost} onClick={() => onChange([...rules, { dimension: "", band: [] }])} data-testid="rule-add">+ Add condition (ALL must match)</button>
    </div>
  );
}

function Archetypes() {
  const [arcs, setArcs] = useState([]);
  const [dims, setDims] = useState([]);
  const [nw, setNw] = useState({ key: "", name: "", description: "", priority: 100, rules: [] });
  const load = useCallback(() => api.get("/admin/archetypes").then((r) => setArcs(r.data.archetypes)), []);
  useEffect(() => { load(); api.get("/admin/dimensions").then((r) => setDims(r.data.dimensions)); }, [load]);
  const save = async (a) => {
    try { await api.patch(`/admin/archetypes/${a.key}`, { name: a.name, description: a.description, active: a.active, priority: Number(a.priority) || 100, assignment_rules: { all: a._rules || [] } }); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const create = async () => {
    if (!nw.key.trim() || !nw.name.trim()) { toast.error("Key and name are required"); return; }
    try {
      await api.post("/admin/archetypes", { key: nw.key.trim(), name: nw.name.trim(), description: nw.description, priority: Number(nw.priority) || 100, active: true, assignment_rules: { all: nw.rules } });
      toast.success("Archetype created"); setNw({ key: "", name: "", description: "", priority: 100, rules: [] }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="archetypes-list">
      <p className="text-xs text-[#6B7280] font-mono">Archetypes are descriptive configurations, not rankings. Rules are deterministic: an archetype is assigned when ALL its conditions match (dimension band ∈ selected). Highest priority (lowest number) wins. No taxonomy is assumed until you define one — real portraits stay unlabelled otherwise.</p>

      <Card testId="archetype-create">
        <p className="cg-eyebrow mb-3">New archetype (V1)</p>
        <div className="grid sm:grid-cols-4 gap-3 mb-3">
          <input className={input} data-testid="arc-new-key" placeholder="key (e.g. grounded_explorer)" value={nw.key} onChange={(e) => setNw({ ...nw, key: e.target.value })} />
          <input className={input} data-testid="arc-new-name" placeholder="Display name" value={nw.name} onChange={(e) => setNw({ ...nw, name: e.target.value })} />
          <input className={input} type="number" placeholder="Priority" value={nw.priority} onChange={(e) => setNw({ ...nw, priority: e.target.value })} />
        </div>
        <textarea className={`${input} h-20 mb-3`} data-testid="arc-new-desc" placeholder="Descriptive summary (not a ranking)" value={nw.description} onChange={(e) => setNw({ ...nw, description: e.target.value })} />
        <p className="cg-eyebrow mb-2">Assignment rules</p>
        <RuleEditor rules={nw.rules} dims={dims} onChange={(r) => setNw({ ...nw, rules: r })} />
        <button className={`${btn} mt-3`} data-testid="arc-new-create" onClick={create}>Create archetype</button>
      </Card>

      {arcs.map((a, i) => (
        <Card key={a.key} testId={`archetype-${a.key}`}>
          <div className="flex items-center justify-between gap-3 mb-2">
            <input className={`${input} font-serif text-lg`} value={a.name} onChange={(e) => { const n = [...arcs]; n[i] = { ...a, name: e.target.value }; setArcs(n); }} />
            <label className="flex items-center gap-2 text-xs text-[#9CA3AF] shrink-0"><input type="checkbox" checked={a.active !== false} onChange={(e) => { const n = [...arcs]; n[i] = { ...a, active: e.target.checked }; setArcs(n); }} className="w-4 h-4 accent-[#D9A25F]" /> active</label>
          </div>
          <p className="font-mono text-xs text-[#6B7280] mb-2">{a.key} · priority {a.priority ?? 100}{a.is_demo ? " · DEMO placeholder" : ""}</p>
          <textarea className={`${input} h-20 mb-3`} value={a.description} onChange={(e) => { const n = [...arcs]; n[i] = { ...a, description: e.target.value }; setArcs(n); }} />
          <p className="cg-eyebrow mb-2">Assignment rules</p>
          <RuleEditor rules={a._rules || (a.assignment_rules?.all || [])} dims={dims} onChange={(r) => { const n = [...arcs]; n[i] = { ...a, _rules: r }; setArcs(n); }} />
          <button className={`${btn} mt-3`} data-testid={`archetype-save-${a.key}`} onClick={() => save(a)}>Save</button>
        </Card>
      ))}
    </div>
  );
}

function Norms() {
  const [versions, setVersions] = useState([]);
  const [dims, setDims] = useState([]);
  const [groups, setGroups] = useState([]);
  const [nw, setNw] = useState({ assessment_version: "", name: "", description: "", n: "", norms: {} });
  const load = useCallback(async () => {
    const r = await api.get("/admin/norm-groups"); setGroups(r.data.norm_groups);
  }, []);
  useEffect(() => {
    load();
    api.get("/admin/dimensions").then((r) => setDims(r.data.dimensions));
    api.get("/admin/versions").then((r) => { setVersions(r.data.versions); const act = r.data.versions.find((v) => v.active); setNw((s) => ({ ...s, assessment_version: s.assessment_version || act?.version || "" })); });
  }, [load]);
  const setNorm = (dim, field, val) => setNw((s) => ({ ...s, norms: { ...s.norms, [dim]: { ...(s.norms[dim] || {}), [field]: val } } }));
  const create = async () => {
    if (!nw.assessment_version || !nw.name.trim()) { toast.error("Version and name required"); return; }
    const dimension_norms = {};
    Object.entries(nw.norms).forEach(([k, v]) => { if (v.mean !== undefined && v.mean !== "" && v.sd) dimension_norms[k] = { mean: Number(v.mean), sd: Number(v.sd) }; });
    try {
      await api.post("/admin/norm-groups", { assessment_version: nw.assessment_version, name: nw.name.trim(), description: nw.description, n: nw.n ? Number(nw.n) : null, dimension_norms });
      toast.success("Norm group saved (unapproved)"); setNw({ ...nw, name: "", description: "", n: "", norms: {} }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleApprove = async (g) => {
    try { await api.patch(`/admin/norm-groups/${g.id}`, { approved: !g.approved }); toast.success(g.approved ? "Approval revoked — percentiles OFF" : "Approved — percentiles will show on new portraits"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (g) => { if (!window.confirm(`Delete norm group "${g.name}"?`)) return; try { await api.delete(`/admin/norm-groups/${g.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-5">
      <Card testId="norms-intro">
        <h3 className="font-serif text-xl text-[#F3F4F6] mb-1">Normative data</h3>
        <p className="text-sm text-[#9CA3AF] leading-relaxed max-w-2xl">Load validated norm groups (per-dimension mean + SD on the 1–5 scale). Norm groups are saved <span className="text-[#F3F4F6]">unapproved</span> and produce <span className="text-[#F3F4F6]">no</span> percentiles anywhere until you explicitly approve one. No population percentiles, cut-scores, or normal/abnormal labels are ever invented. Once approved, percentiles appear on newly generated portraits with zero code changes.</p>
      </Card>

      <Card testId="norms-create">
        <p className="cg-eyebrow mb-3">New norm group</p>
        <div className="grid sm:grid-cols-4 gap-3 mb-3">
          <select className={input} data-testid="norm-version" value={nw.assessment_version} onChange={(e) => setNw({ ...nw, assessment_version: e.target.value })}>
            <option value="">Version…</option>{versions.map((v) => <option key={v.version} value={v.version}>{v.display_name || v.version}</option>)}
          </select>
          <input className={input} data-testid="norm-name" placeholder="Norm group name" value={nw.name} onChange={(e) => setNw({ ...nw, name: e.target.value })} />
          <input className={input} type="number" data-testid="norm-n" placeholder="Sample size n" value={nw.n} onChange={(e) => setNw({ ...nw, n: e.target.value })} />
        </div>
        <input className={`${input} mb-3`} placeholder="Description / source (optional)" value={nw.description} onChange={(e) => setNw({ ...nw, description: e.target.value })} />
        <p className="cg-eyebrow mb-2">Per-dimension mean &amp; SD (1–5 scale)</p>
        <div className="space-y-2 mb-3">
          {dims.map((d) => (
            <div key={d.key} className="flex items-center gap-3">
              <span className="text-sm text-[#9CA3AF] w-56 shrink-0">{d.name}</span>
              <input className={`${input} max-w-[120px]`} type="number" step="0.01" data-testid={`norm-mean-${d.key}`} placeholder="mean" value={nw.norms[d.key]?.mean ?? ""} onChange={(e) => setNorm(d.key, "mean", e.target.value)} />
              <input className={`${input} max-w-[120px]`} type="number" step="0.01" data-testid={`norm-sd-${d.key}`} placeholder="SD" value={nw.norms[d.key]?.sd ?? ""} onChange={(e) => setNorm(d.key, "sd", e.target.value)} />
            </div>
          ))}
        </div>
        <button className={btn} data-testid="norm-create" onClick={create}>Save norm group (unapproved)</button>
      </Card>

      <Card testId="norms-list">
        <h3 className="font-serif text-xl text-[#F3F4F6] mb-3">Norm groups</h3>
        {groups.length === 0 && <p className="text-sm text-[#9CA3AF]">No norm groups yet. Percentiles remain OFF until a norm group is created and approved.</p>}
        <div className="space-y-2">
          {groups.map((g) => (
            <div key={g.id} className="flex items-center justify-between border-b border-[var(--cg-border)] py-3 last:border-0" data-testid={`norm-row-${g.id}`}>
              <div>
                <p className="text-sm text-[#E8EAEE]">{g.name} {g.approved ? <span className="text-[#7D9D86]">· approved</span> : <span className="text-[#6B7280]">· unapproved</span>}</p>
                <p className="font-mono text-xs text-[#6B7280]">{g.assessment_version}{g.n ? ` · n=${g.n}` : ""} · {Object.keys(g.dimension_norms || {}).length} dimensions</p>
              </div>
              <div className="flex gap-2">
                <button className={btnGhost} data-testid={`norm-approve-${g.id}`} onClick={() => toggleApprove(g)}>{g.approved ? "Revoke" : "Approve"}</button>
                <button className={btnGhost} data-testid={`norm-delete-${g.id}`} onClick={() => del(g)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Prompts() {
  const [prompts, setPrompts] = useState([]);
  const [edit, setEdit] = useState({ individual: "", organizational: "" });
  const load = useCallback(async () => {
    const r = await api.get("/admin/prompts"); setPrompts(r.data.prompts);
    const ind = r.data.prompts.find((p) => p.type === "individual" && p.active);
    const org = r.data.prompts.find((p) => p.type === "organizational" && p.active);
    setEdit({ individual: ind?.prompt_text || "", organizational: org?.prompt_text || "" });
  }, []);
  useEffect(() => { load(); }, [load]);
  const saveNew = async (type) => { try { await api.post("/admin/prompts", { type, prompt_text: edit[type] }); toast.success(`New ${type} prompt version activated`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-5">
      {["individual", "organizational"].map((type) => (
        <Card key={type} testId={`prompt-${type}`}>
          <h3 className="font-serif text-xl text-[#F3F4F6] mb-1 capitalize">{type} interpretation prompt</h3>
          <p className="text-xs text-[#6B7280] mb-3 font-mono">Saving creates a new version & activates it. Historical reports keep their original prompt version.</p>
          <textarea data-testid={`prompt-textarea-${type}`} className={`${input} font-mono h-64`} value={edit[type]} onChange={(e) => setEdit({ ...edit, [type]: e.target.value })} />
          <button className={`${btn} mt-3`} data-testid={`prompt-save-${type}`} onClick={() => saveNew(type)}>Save as new version</button>
        </Card>
      ))}
      <Card testId="prompt-versions">
        <h3 className="font-serif text-xl text-[#F3F4F6] mb-3">Prompt versions</h3>
        {prompts.map((p) => <p key={p.version} className="font-mono text-xs text-[#9CA3AF] py-1">{p.version} · {p.type} {p.active && <span className="text-brass">· active</span>}</p>)}
      </Card>
    </div>
  );
}

function Inspector() {
  const [sessions, setSessions] = useState([]);
  const [sel, setSel] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/sessions").then((r) => setSessions(r.data.sessions)); }, []);
  const inspect = async (sid) => { setSel(sid); const r = await api.get(`/admin/inspect/${sid}`); setData(r.data); };
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <Card testId="inspector-sessions">
        <h3 className="font-serif text-xl text-[#F3F4F6] mb-3">Completed sessions</h3>
        <div className="space-y-1 max-h-[560px] overflow-y-auto" data-testid="inspector-session-list">
          {sessions.map((s) => (
            <button key={s.id} data-testid={`inspect-session-${s.id}`} onClick={() => inspect(s.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs font-mono cg-scale-btn ${sel === s.id ? "bg-[rgba(217,162,95,0.12)] text-[#F3F4F6]" : "text-[#9CA3AF] hover:bg-[#222634]"}`}>
              {s.id.slice(0, 8)} · {(s.completed_at || "").slice(0, 10)} {s.is_demo && "· demo"}
            </button>
          ))}
        </div>
      </Card>
      <div className="lg:col-span-2">
        {data ? (
          <Card testId="inspector-detail">
            <h3 className="font-serif text-xl text-[#F3F4F6] mb-3">Scoring inspection</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-xs font-mono">
              {[["Assessment", data.assessment_version], ["Scoring", data.scoring_version], ["AI model", data.ai_model || "—"], ["Prompt", data.ai_prompt_version || "—"]].map(([l, v]) => (
                <div key={l} className="bg-[#0D0E12] rounded-lg p-3"><p className="cg-eyebrow mb-1">{l}</p><p className="text-[#E8EAEE] break-all">{v}</p></div>
              ))}
            </div>
            <div className="mb-4">
              <p className="cg-eyebrow mb-2">Dimension calculations</p>
              <div className="space-y-1">
                {Object.values(data.scores.dimension_scores).map((d) => (
                  <div key={d.dimension} className="flex justify-between text-xs font-mono border-b border-[var(--cg-border)] py-1.5">
                    <span className="text-[#9CA3AF]">{d.name}</span>
                    <span className="text-[#E8EAEE]">raw {d.raw_mean} · aligned {d.aligned_mean} · {d.band} · n{d.n_items}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="cg-eyebrow mb-2">Item inspection (raw → reverse → applied)</p>
            <div className="max-h-72 overflow-y-auto space-y-1" data-testid="inspector-items">
              {data.scores.item_inspection.map((r) => (
                <div key={r.item_id} className="flex justify-between text-xs font-mono border-b border-[var(--cg-border)] py-1">
                  <span className="text-[#9CA3AF]">{r.item_code} · {r.dimension}</span>
                  <span className="text-[#E8EAEE]">raw {r.raw_response} {r.reverse_scored ? "· rev" : ""} · w{r.weight} → {r.applied_value}</span>
                </div>
              ))}
            </div>
          </Card>
        ) : <Card><p className="text-sm text-[#9CA3AF]">Select a session to inspect raw responses, reverse scoring, weights, dimension calculations, and versions. Visible to super-admin only.</p></Card>}
      </div>
    </div>
  );
}

function Research() {
  const [includeOrg, setIncludeOrg] = useState(false);
  const download = async () => {
    const token = localStorage.getItem("cg_token");
    const r = await fetch(`${API}/admin/research-export.csv?include_org=${includeOrg}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "research-export-deidentified.csv"; a.click(); URL.revokeObjectURL(url);
    toast.success("Export downloaded");
  };
  return (
    <Card testId="research-card">
      <h3 className="font-serif text-xl text-[#F3F4F6] mb-2">De-identified research export</h3>
      <p className="text-sm text-[#9CA3AF] mb-4 max-w-2xl leading-relaxed">Exports anonymous respondent IDs, item IDs, numeric responses, applied values, dimension assignments, territories, and assessment/scoring versions. No names or emails are ever included. Stable machine-readable columns include version information so instrument versions are never unknowingly combined.</p>
      <label className="flex items-center gap-3 mb-5 cursor-pointer">
        <input type="checkbox" data-testid="research-include-org" checked={includeOrg} onChange={(e) => setIncludeOrg(e.target.checked)} className="w-4 h-4 accent-[#D9A25F]" />
        <span className="text-sm text-[#9CA3AF]">Include organization / cohort identifiers (only where ethically &amp; contractually permitted)</span>
      </label>
      <button className={btn} data-testid="research-download-button" onClick={download}>Download CSV</button>
    </Card>
  );
}

const FIELD_KEYS = [
  ["department", "Department / Business Unit"],
  ["job_title", "Job Title"],
  ["org_level", "Career / Organizational Level"],
  ["location", "Location / Region"],
  ["team", "Team / Function"],
  ["tenure", "Tenure with Organization"],
];

function WelcomePages() {
  const [camps, setCamps] = useState([]);
  const [sel, setSel] = useState("");
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/campaigns").then((r) => setCamps(r.data.campaigns)).catch(() => {}); }, []);
  const loadCfg = async (id) => {
    setSel(id); setCfg(null);
    if (!id) return;
    const { data } = await api.get(`/admin/campaigns/${id}/participant-config`);
    const o = data.participant_config || {};
    const fields = {};
    FIELD_KEYS.forEach(([k, label]) => {
      const f = (o.fields || {})[k] || {};
      fields[k] = { state: f.state || (k === "location" || k === "team" ? "off" : "optional"), options: (f.options || []).join(", ") };
    });
    setCfg({ welcome: { heading: o.welcome?.heading || "", message: o.welcome?.message || "", logo_url: o.welcome?.logo_url || "", contact: o.welcome?.contact || "" }, fields });
  };
  const save = async () => {
    setBusy(true);
    try {
      const fields = {};
      FIELD_KEYS.forEach(([k]) => { fields[k] = { state: cfg.fields[k].state, options: cfg.fields[k].options.split(",").map((s) => s.trim()).filter(Boolean) }; });
      await api.put(`/admin/campaigns/${sel}/participant-config`, { welcome: cfg.welcome, fields });
      toast.success("Welcome page saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card testId="welcome-card">
      <h3 className="font-serif text-xl text-[#F3F4F6] mb-1">Organization welcome pages</h3>
      <p className="text-sm text-[#9CA3AF] mb-5 max-w-2xl leading-relaxed">Configure the per-campaign welcome page and which organizational role fields participants see. Each field can be Off, Optional or Required. Privacy thresholds still apply to all aggregate reporting.</p>
      <select className={input} data-testid="welcome-campaign-select" value={sel} onChange={(e) => loadCfg(e.target.value)}>
        <option value="">Select a campaign…</option>
        {camps.map((c) => <option key={c.id} value={c.id}>{c.organization_name} · {c.name} ({c.code}){c.has_config ? " ✓" : ""}</option>)}
      </select>
      {cfg && (
        <div className="mt-6 space-y-5" data-testid="welcome-editor">
          <div className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-xl p-5 space-y-3">
            <p className="cg-eyebrow">Welcome page</p>
            <input className={input} placeholder="Welcome heading" data-testid="welcome-heading-input" value={cfg.welcome.heading} onChange={(e) => setCfg({ ...cfg, welcome: { ...cfg.welcome, heading: e.target.value } })} />
            <textarea className={`${input} min-h-[110px]`} placeholder="Welcome message (leave blank for the standard Caesar's Ghost template)" data-testid="welcome-message-input" value={cfg.welcome.message} onChange={(e) => setCfg({ ...cfg, welcome: { ...cfg.welcome, message: e.target.value } })} />
            <div className="grid sm:grid-cols-2 gap-3">
              <input className={input} placeholder="Logo URL (optional)" value={cfg.welcome.logo_url} onChange={(e) => setCfg({ ...cfg, welcome: { ...cfg.welcome, logo_url: e.target.value } })} />
              <input className={input} placeholder="Contact info (optional)" value={cfg.welcome.contact} onChange={(e) => setCfg({ ...cfg, welcome: { ...cfg.welcome, contact: e.target.value } })} />
            </div>
          </div>
          <div className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-xl p-5 space-y-3">
            <p className="cg-eyebrow">Organizational role fields</p>
            {FIELD_KEYS.map(([k, label]) => (
              <div key={k} className="grid grid-cols-[1fr_auto] sm:grid-cols-[180px_130px_1fr] gap-3 items-center">
                <span className="text-sm text-[#E8EAEE]">{label}</span>
                <select className={input} data-testid={`welcome-field-${k}`} value={cfg.fields[k].state} onChange={(e) => setCfg({ ...cfg, fields: { ...cfg.fields, [k]: { ...cfg.fields[k], state: e.target.value } } })}>
                  <option value="off">Off</option><option value="optional">Optional</option><option value="required">Required</option>
                </select>
                <input className={`${input} col-span-2 sm:col-span-1`} placeholder="Dropdown options (comma separated, optional)" value={cfg.fields[k].options} onChange={(e) => setCfg({ ...cfg, fields: { ...cfg.fields, [k]: { ...cfg.fields[k], options: e.target.value } } })} />
              </div>
            ))}
          </div>
          <button className={btn} data-testid="welcome-save" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save welcome page"}</button>
        </div>
      )}
    </Card>
  );
}

function Promos() {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState({ code: "", emails: "", max_uses: 1, expires_at: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/promos").then((r) => setPromos(r.data.promos)).catch(() => {});
  useEffect(() => { load(); }, []);
  const parseEmails = (text) => Array.from(new Set(((text || "").match(/[^\s,;]+@[^\s,;]+/g) || []).map((e) => e.toLowerCase())));
  const create = async () => {
    const emails = parseEmails(form.emails);
    if (!form.code.trim() || emails.length === 0) { toast.error("Enter a code and at least one email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/promos", { code: form.code.trim(), emails, max_uses: Number(form.max_uses) || 1, expires_at: form.expires_at || null });
      toast.success(`Saved: ${data.created} new, ${data.updated} updated for ${data.code}`);
      setForm({ code: "", emails: "", max_uses: 1, expires_at: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const onCsv = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const emails = parseEmails(await f.text());
    setForm((p) => ({ ...p, emails: Array.from(new Set([...parseEmails(p.emails), ...emails])).join("\n") }));
    toast.success(`${emails.length} emails loaded from CSV`); e.target.value = "";
  };
  const patch = async (id, body) => { try { await api.patch(`/admin/promos/${id}`, body); load(); } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); } };
  const exportCsv = async () => {
    const token = localStorage.getItem("cg_token");
    const r = await fetch(`${API}/admin/promos.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "promo-authorizations.csv"; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <Card testId="promos-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-serif text-xl text-[#F3F4F6]">Individual promo codes</h3>
        <button className="text-sm text-brass hover:opacity-80" data-testid="promos-export" onClick={exportCsv}>Export CSV</button>
      </div>
      <p className="text-sm text-[#9CA3AF] mb-5 max-w-2xl leading-relaxed">A promo code only works for the authenticated account whose email you authorize here. One code can authorize many emails; each redemption is tracked separately and is session-specific. Organization codes are managed under Organizations.</p>
      <div className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-xl p-5 mb-6 space-y-3" data-testid="promo-create">
        <div className="grid sm:grid-cols-3 gap-3">
          <input className={input} data-testid="promo-new-code" placeholder="Promo code e.g. LAUNCH2026" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <input className={input} data-testid="promo-new-maxuses" type="number" min="1" placeholder="Max uses per email" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} />
          <input className={input} data-testid="promo-new-expires" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
        </div>
        <textarea className={`${input} min-h-[90px] font-mono text-sm`} data-testid="promo-new-emails" placeholder="Authorized emails — paste a list separated by commas, spaces or new lines" value={form.emails} onChange={(e) => setForm({ ...form, emails: e.target.value })} />
        <div className="flex flex-wrap items-center gap-3">
          <button className={btn} data-testid="promo-create-button" onClick={create} disabled={busy}>{busy ? "Saving…" : "Create / update authorizations"}</button>
          <label className="text-sm text-brass cursor-pointer hover:opacity-80">Upload CSV of emails<input type="file" accept=".csv,text/csv" className="hidden" data-testid="promo-csv-upload" onChange={onCsv} /></label>
        </div>
      </div>
      <div className="space-y-1 max-h-[520px] overflow-y-auto" data-testid="promos-list">
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 text-xs text-[#6B7280] font-mono px-1 pb-1">
          <span>Code · email</span><span className="text-right">Uses</span><span className="text-right">Expires</span><span className="text-right">Status</span><span className="text-right">Actions</span>
        </div>
        {promos.map((p) => (
          <div key={p.id} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 items-center border-b border-[var(--cg-border)] py-2 text-sm">
            <span className="text-[#E8EAEE] truncate"><span className="font-mono text-brass">{p.code}</span> · {p.email}{p.redeemed && <span className="text-[#6B7280]"> · redeemed</span>}</span>
            <span className="text-right font-mono text-[#9CA3AF]">{p.uses}/{p.max_uses}</span>
            <span className="text-right font-mono text-xs text-[#6B7280]">{p.expires_at ? String(p.expires_at).slice(0, 10) : "—"}</span>
            <span className={`text-right text-xs ${p.status === "active" ? "text-[#7D9D86]" : p.status === "expired" ? "text-amber2" : "text-[#6B7280]"}`}>{(p.status || (p.active ? "active" : "revoked")).replace("_", " ")}</span>
            <span className="text-right flex gap-2 justify-end">
              <button className="text-xs text-brass hover:opacity-80" data-testid={`promo-toggle-${p.id}`} onClick={() => patch(p.id, { active: !p.active })}>{p.active ? "Revoke" : "Enable"}</button>
              <button className="text-xs text-[#9CA3AF] hover:text-brass" data-testid={`promo-reset-${p.id}`} onClick={() => patch(p.id, { reset_uses: true })}>Reset</button>
            </span>
          </div>
        ))}
        {promos.length === 0 && <p className="text-sm text-[#6B7280] py-4">No promo authorizations yet.</p>}
      </div>
    </Card>
  );
}

const KIND_LABEL = { paid: "Stripe", gift: "Gift", promo: "Promo" };

function PurchaseRow({ t, money }) {
  const a = t.assessment;
  const ok = ["paid", "redeemed"].includes(t.payment_status);
  return (
    <div data-testid={`purchase-row-${t.session_id}`} className="grid grid-cols-2 md:grid-cols-[1.4fr_auto_1.4fr_auto_auto_auto] gap-x-3 gap-y-1 items-center border-b border-[var(--cg-border)] py-2 text-sm">
      <span className="text-[#E8EAEE] truncate" data-testid="purchase-participant">{t.email || t.user_id}</span>
      <span className="text-xs font-mono text-brass" data-testid="purchase-kind">
        {KIND_LABEL[t.kind] || t.kind}{t.discount_code ? ` · ${t.discount_code}` : ""}
      </span>
      <span className="text-xs text-[#9CA3AF] truncate" data-testid="purchase-assessment">
        {a ? `${a.id.slice(0, 8)} · ${a.status === "completed" ? `completed ${(a.completed_at || "").slice(0, 10)}` : "in progress"}` : ok ? "Not started yet" : "—"}
      </span>
      <span className="md:text-right font-mono text-[#9CA3AF]" data-testid="purchase-amount">{money(t.amount_paid ?? t.amount, t.currency)}</span>
      <span className={`md:text-right text-xs ${ok ? "text-[#7D9D86]" : t.payment_status === "pending" ? "text-[#9CA3AF]" : "text-terracotta"}`} data-testid="purchase-status">{t.payment_status}</span>
      <span className="md:text-right font-mono text-xs text-[#6B7280]">{(t.updated_at || t.created_at || "").slice(0, 10)}</span>
    </div>
  );
}

function Payments() {
  const [data, setData] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [giftLabel, setGiftLabel] = useState("");
  const [giftBusy, setGiftBusy] = useState(false);
  const [newGift, setNewGift] = useState(null);
  const loadGifts = () => api.get("/admin/payments/gifts").then((r) => setGifts(r.data.gifts)).catch(() => {});
  useEffect(() => { api.get("/admin/payments").then((r) => setData(r.data)).catch(() => {}); loadGifts(); }, []);
  const generateGift = async () => {
    setGiftBusy(true);
    try {
      const { data: g } = await api.post("/admin/payments/gift", { label: giftLabel || undefined });
      setNewGift(g); setGiftLabel(""); loadGifts();
      toast.success("Complimentary link created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGiftBusy(false); }
  };
  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Link copied"); };
  if (!data) return <Card testId="payments-card"><p className="cg-eyebrow animate-pulse">Loading…</p></Card>;
  const s = data.summary;
  const money = (cents, cur) => `${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${(cur || "").toUpperCase()}`;
  return (
    <Card testId="payments-card">
      <h3 className="font-serif text-xl text-[#F3F4F6] mb-1">Individual purchases</h3>
      <p className="text-sm text-[#9CA3AF] mb-5 max-w-2xl leading-relaxed">One-time assessment unlocks by independent participants: Stripe purchases, complimentary gift links and email promo codes, with the assessment each one unlocked. Organization participants are covered by their campaigns and do not appear here.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-xl p-4" data-testid="payments-revenue">
          <p className="cg-eyebrow mb-1">Revenue</p><p className="font-serif text-2xl text-[#F3F4F6]">{s.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })} {s.currency}</p>
        </div>
        <div className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-xl p-4" data-testid="payments-paid-count">
          <p className="cg-eyebrow mb-1">Paid</p><p className="font-serif text-2xl text-[#F3F4F6]">{s.paid_count}</p>
        </div>
        <div className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-xl p-4" data-testid="payments-total-count">
          <p className="cg-eyebrow mb-1">Checkout sessions</p><p className="font-serif text-2xl text-[#F3F4F6]">{s.total_count}</p>
        </div>
        <div className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-xl p-4" data-testid="payments-free-count">
          <p className="cg-eyebrow mb-1">Promo / gift access</p><p className="font-serif text-2xl text-[#F3F4F6]">{(s.promo_count || 0) + (s.gift_count || 0)}</p>
        </div>
      </div>

      <div className="bg-[#0D0E12] border border-brass/30 rounded-xl p-5 mb-6" data-testid="gift-panel">
        <div className="flex items-center gap-2 mb-1"><Gift className="w-4 h-4 text-brass" /><h4 className="font-serif text-lg text-[#F3F4F6]">Complimentary access</h4></div>
        <p className="text-sm text-[#9CA3AF] mb-4 max-w-2xl">Generate a single-use, 100% off link for a pilot partner, press contact or guest. Each link works once and never exposes a global code.</p>
        <div className="flex flex-wrap gap-3 items-center">
          <input className={input} style={{ maxWidth: 320 }} data-testid="gift-label-input" placeholder="Label (optional) e.g. Press — Jane Doe" value={giftLabel} onChange={(e) => setGiftLabel(e.target.value)} />
          <button className={btn} data-testid="gift-generate-button" onClick={generateGift} disabled={giftBusy}>{giftBusy ? "Creating…" : "Generate link"}</button>
        </div>
        {newGift && (
          <div className="mt-4 bg-[#14161F] border border-[var(--cg-border)] rounded-lg p-4" data-testid="gift-new-result">
            <p className="cg-eyebrow mb-2">New complimentary link · code {newGift.code}</p>
            <div className="flex flex-wrap items-center gap-3">
              <code className="text-sm text-[#E8EAEE] break-all flex-1 min-w-[200px]" data-testid="gift-new-url">{newGift.url}</code>
              <button className="text-sm text-brass hover:opacity-80 inline-flex items-center gap-1" data-testid="gift-copy-button" onClick={() => copy(newGift.url)}><Copy className="w-4 h-4" /> Copy</button>
            </div>
          </div>
        )}
        {gifts.length > 0 && (
          <div className="mt-4 space-y-1" data-testid="gift-list">
            <p className="cg-eyebrow mb-1">Existing complimentary codes · {gifts.length}</p>
            {gifts.map((g) => (
              <div key={g.code} className="flex flex-wrap justify-between items-center gap-2 border-b border-[var(--cg-border)] py-2 text-sm">
                <span className="font-mono text-[#E8EAEE]">{g.code}{g.label ? <span className="text-[#6B7280]"> · {g.label}</span> : null}</span>
                <span className="flex items-center gap-3">
                  <span className={`text-xs ${g.times_redeemed >= (g.max_redemptions || 1) || !g.active ? "text-[#6B7280]" : "text-[#7D9D86]"}`}>{g.times_redeemed}/{g.max_redemptions || 1} used{g.active ? "" : " · inactive"}</span>
                  <button className="text-xs text-brass hover:opacity-80 inline-flex items-center gap-1" onClick={() => copy(g.url)}><Copy className="w-3.5 h-3.5" /> Copy link</button>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-1 max-h-[520px] overflow-y-auto" data-testid="payments-list">
        <div className="hidden md:grid grid-cols-[1.4fr_auto_1.4fr_auto_auto_auto] gap-3 text-xs text-[#6B7280] font-mono px-1 pb-1">
          <span>Participant</span><span>Type</span><span>Assessment unlocked</span><span className="text-right">Amount</span><span className="text-right">Status</span><span className="text-right">Date</span>
        </div>
        {data.transactions.map((t) => <PurchaseRow key={t.session_id} t={t} money={money} />)}
        {data.transactions.length === 0 && <p className="text-sm text-[#6B7280] py-4">No purchases yet.</p>}
      </div>
    </Card>
  );
}

function Audit() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setLogs(r.data.logs)); }, []);
  return (
    <Card testId="audit-card">
      <h3 className="font-serif text-xl text-[#F3F4F6] mb-4">Audit log · latest {logs.length}</h3>
      <div className="space-y-1 max-h-[600px] overflow-y-auto" data-testid="audit-list">
        {logs.map((l) => (
          <div key={l.id} className="flex justify-between text-xs font-mono border-b border-[var(--cg-border)] py-2">
            <span className="text-[#E8EAEE]">{l.action}</span>
            <span className="text-[#6B7280]">{l.target_type || ""} · {(l.created_at || "").slice(0, 19).replace("T", " ")}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}


function fmtDuration(sec) {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60), s = Math.round(sec % 60);
  return m ? `${m}m ${s}s` : `${s}s`;
}

function Pilot() {
  const [version, setVersion] = useState("cg-v1-pilot");
  const [versions, setVersions] = useState([]);
  const [ov, setOv] = useState(null);
  const [flagDetail, setFlagDetail] = useState([]);
  const [fbList, setFbList] = useState([]);
  const [themes, setThemes] = useState(null);
  const [themesBusy, setThemesBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/pilot/admin/overview?version=${version}`).then((r) => setOv(r.data)).catch(() => setOv(null));
    api.get(`/pilot/admin/item-flags?version=${version}`).then((r) => setFlagDetail(r.data.items)).catch(() => {});
    api.get(`/pilot/admin/feedback?version=${version}`).then((r) => setFbList(r.data.feedback)).catch(() => {});
  }, [version]);
  useEffect(() => { api.get("/admin/versions").then((r) => setVersions(r.data.versions || [])).catch(() => {}); }, []);
  useEffect(() => { load(); setThemes(null); }, [load]);
  const summarizeThemes = async (refresh = false) => {
    setThemesBusy(true);
    try {
      const r = await api.post(`/pilot/admin/feedback-themes?version=${version}&refresh=${refresh}`);
      setThemes(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setThemesBusy(false); }
  };
  const exportCsv = async () => {
    const token = localStorage.getItem("cg_token");
    const r = await fetch(`${API}/pilot/admin/export.csv?version=${version}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Could not export"); return; }
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "pilot-export.csv"; a.click(); URL.revokeObjectURL(url);
  };
  const Stat = ({ label, value, tid }) => (
    <div className="cg-card p-5" data-testid={tid}><p className="cg-eyebrow mb-1">{label}</p><p className="font-serif text-3xl text-[#F3F4F6]">{value}</p></div>
  );
  return (
    <div className="space-y-6" data-testid="pilot-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="cg-eyebrow">Assessment</span>
          <select data-testid="pilot-version-select" className={`${input} max-w-[280px]`} value={version} onChange={(e) => setVersion(e.target.value)}>
            {(versions.length ? versions : [{ version: "cg-v1-pilot", display_name: "Caesar's Ghost V1" }]).map((v) => (
              <option key={v.version} value={v.version}>{v.display_name || v.version}</option>
            ))}
          </select>
        </div>
        <button data-testid="pilot-export-button" className={btnGhost} onClick={exportCsv}>Export de-identified CSV</button>
      </div>
      {!ov ? <p className="cg-eyebrow animate-pulse">Loading…</p> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="Started" value={ov.started} tid="pilot-started" />
            <Stat label="Completed" value={ov.completed} tid="pilot-completed" />
            <Stat label="Completion rate" value={`${Math.round(ov.completion_rate * 100)}%`} tid="pilot-rate" />
            <Stat label="Incomplete" value={ov.incomplete} tid="pilot-incomplete" />
            <Stat label="Median time" value={fmtDuration(ov.median_completion_seconds)} tid="pilot-median" />
            <Stat label="Avg time" value={fmtDuration(ov.avg_completion_seconds)} tid="pilot-avg" />
            <Stat label="Resume events" value={ov.resume_events} tid="pilot-resume" />
            <Stat label="Total item flags" value={ov.total_flags} tid="pilot-flags" />
          </div>

          <Card testId="pilot-recognizable">
            <h3 className="font-serif text-xl text-[#F3F4F6] mb-1">"Did this Portrait feel recognizable?"</h3>
            <p className="text-sm text-[#6B7280] mb-4">{ov.feedback_count} responses · average {ov.recognizable_avg ?? "—"} / 5</p>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((n) => {
                const c = ov.recognizable_distribution?.[n] || 0;
                const max = Math.max(1, ...Object.values(ov.recognizable_distribution || { 0: 1 }));
                return (
                  <div key={n} className="flex items-center gap-3 text-sm">
                    <span className="w-4 font-mono text-[#9CA3AF]">{n}</span>
                    <div className="flex-1 h-3 rounded-full bg-[#0D0E12]"><div className="h-3 rounded-full bg-brass" style={{ width: `${(c / max) * 100}%` }} /></div>
                    <span className="w-8 text-right font-mono text-[#6B7280]">{c}</span>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card testId="pilot-most-flagged">
            <h3 className="font-serif text-xl text-[#F3F4F6] mb-1">Most frequently flagged statements</h3>
            <p className="text-sm text-[#6B7280] mb-4">Helps identify items that repeatedly create response difficulty during psychometric review.</p>
            {(!flagDetail.length) ? <p className="text-sm text-[#9CA3AF]">No statements flagged yet.</p> : (
              <div className="space-y-3">
                {flagDetail.slice(0, 15).map((f) => (
                  <div key={f.item_id} className="border-b border-[var(--cg-border)] pb-3" data-testid={`pilot-flag-${f.item_id}`}>
                    <div className="flex justify-between gap-3">
                      <p className="text-sm text-[#E8EAEE]">{f.item_text || f.item_id}</p>
                      <span className="text-brass font-mono text-sm shrink-0">{f.count}×</span>
                    </div>
                    <p className="text-xs text-[#6B7280] font-mono mt-1">{Object.entries(f.reasons).map(([r, c]) => `${r}: ${c}`).join(" · ")}</p>
                    {f.comments?.length > 0 && <p className="text-xs text-[#9CA3AF] mt-1 italic">"{f.comments[0]}"</p>}
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card testId="pilot-feedback-themes">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
              <h3 className="font-serif text-xl text-[#F3F4F6] flex items-center gap-2"><Sparkles className="w-5 h-5 text-brass" /> AI feedback themes</h3>
              <div className="flex gap-2">
                <button data-testid="feedback-themes-button" onClick={() => summarizeThemes(false)} disabled={themesBusy}
                  className={btnGhost}>{themesBusy ? "Analyzing…" : themes ? "View / refresh cache" : "Summarize themes"}</button>
                {themes && <button data-testid="feedback-themes-refresh" onClick={() => summarizeThemes(true)} disabled={themesBusy} className={btnGhost}>Re-analyze</button>}
              </div>
            </div>
            <p className="text-sm text-[#6B7280] mb-4">Claude Sonnet 4.6 groups de-identified open-text feedback into recurring themes for instrument development. Qualitative — not a clinical or psychometric conclusion.</p>
            {!themes ? (
              <p className="text-sm text-[#9CA3AF]">Click "Summarize themes" to analyze all open-text feedback.</p>
            ) : (
              <div className="space-y-4" data-testid="feedback-themes-result">
                {themes.overall_note && <p className="text-[#D6D9DF] leading-relaxed border-l-2 border-brass pl-4">{themes.overall_note}</p>}
                <p className="text-xs text-[#6B7280] font-mono">{themes.analyzed_count} snippets analyzed{themes.cached ? " · cached" : ""}{themes.generated_at ? ` · ${new Date(themes.generated_at).toLocaleString()}` : ""}</p>
                {(themes.themes || []).length === 0 && <p className="text-sm text-[#9CA3AF]">Not enough feedback yet to identify themes.</p>}
                {(themes.themes || []).map((t, i) => (
                  <div key={i} className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg p-4" data-testid={`feedback-theme-${i}`}>
                    <div className="flex justify-between gap-3 items-baseline">
                      <p className="text-[#F3F4F6] font-medium">{t.title}</p>
                      {typeof t.count === "number" && <span className="text-brass font-mono text-sm shrink-0">~{t.count}</span>}
                    </div>
                    <p className="text-sm text-[#B9BDC7] mt-1 leading-relaxed">{t.description}</p>
                    {(t.quotes || []).map((q, j) => <p key={j} className="text-xs text-[#9CA3AF] italic mt-1.5">"{q}"</p>)}
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card testId="pilot-feedback-summaries">
            <h3 className="font-serif text-xl text-[#F3F4F6] mb-1">Participant feedback (de-identified)</h3>
            <p className="text-sm text-[#6B7280] mb-4">{fbList.length} feedback entries. Individual identities are not shown.</p>
            <div className="space-y-3 max-h-[420px] overflow-y-auto">
              {fbList.filter((f) => f.confusing || f.inaccurate || f.surprised || f.difficult_items || f.final_comments).map((f, i) => (
                <div key={i} className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg p-3 text-sm space-y-1" data-testid={`pilot-feedback-${i}`}>
                  {f.recognizable && <p className="text-xs text-[#6B7280] font-mono">Recognizable: {f.recognizable}/5</p>}
                  {f.confusing && <p className="text-[#B9BDC7]"><span className="text-[#6B7280]">Confusing:</span> {f.confusing}</p>}
                  {f.inaccurate && <p className="text-[#B9BDC7]"><span className="text-[#6B7280]">Inaccurate:</span> {f.inaccurate}</p>}
                  {f.surprised && <p className="text-[#B9BDC7]"><span className="text-[#6B7280]">Surprised:</span> {f.surprised}</p>}
                  {f.difficult_items && <p className="text-[#B9BDC7]"><span className="text-[#6B7280]">Difficult items:</span> {f.difficult_items}</p>}
                  {f.final_comments && <p className="text-[#B9BDC7]"><span className="text-[#6B7280]">Final:</span> {f.final_comments}</p>}
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function ContentEditor() {
  const [dims, setDims] = useState([]);
  const [explanations, setExplanations] = useState({});
  const [howToRead, setHowToRead] = useState("");
  const [privacy, setPrivacy] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/config/dimensions").then((r) => setDims(r.data.dimensions || [])).catch(() => {});
    api.get("/admin/content").then((r) => {
      setExplanations(r.data.dimension_explanations || {});
      setHowToRead(r.data.how_to_read || "");
      setPrivacy(r.data.privacy_statement || "");
    }).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/content", { dimension_explanations: explanations, how_to_read: howToRead, privacy_statement: privacy });
      toast.success("Participant copy saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const ta = "w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2.5 text-sm text-[#F3F4F6] focus:border-brass outline-none";
  return (
    <div className="space-y-6" data-testid="content-editor">
      <Card testId="content-dimensions">
        <h3 className="font-serif text-xl text-[#F3F4F6] mb-1">What the Seven Dimensions Mean</h3>
        <p className="text-sm text-[#6B7280] mb-4">Plain-language explanations shown to participants (Grade 7–8 reading level).</p>
        <div className="space-y-4">
          {dims.map((d) => (
            <label key={d.key} className="block" data-testid={`content-dim-${d.key}`}>
              <span className="text-sm text-[#E8EAEE]">{d.name}</span>
              <textarea rows={2} className={`${ta} mt-1.5`} value={explanations[d.key] || ""}
                onChange={(e) => setExplanations({ ...explanations, [d.key]: e.target.value })} />
            </label>
          ))}
        </div>
      </Card>
      <Card testId="content-howtoread">
        <h3 className="font-serif text-xl text-[#F3F4F6] mb-1">How to Read Your Authority Portrait</h3>
        <p className="text-sm text-[#6B7280] mb-4">Shown before results. Use blank lines to separate paragraphs.</p>
        <textarea rows={7} className={ta} value={howToRead} onChange={(e) => setHowToRead(e.target.value)} data-testid="content-howtoread-input" />
      </Card>
      <Card testId="content-privacy">
        <h3 className="font-serif text-xl text-[#F3F4F6] mb-1">Privacy statement</h3>
        <p className="text-sm text-[#6B7280] mb-4">Use <code className="text-brass">{"{first_name}"}</code> where the participant's first name should appear.</p>
        <textarea rows={4} className={ta} value={privacy} onChange={(e) => setPrivacy(e.target.value)} data-testid="content-privacy-input" />
      </Card>
      <button data-testid="content-save-button" className={btn} onClick={save} disabled={busy}>{busy ? "Saving…" : "Save participant copy"}</button>
    </div>
  );
}

function BrandSettings() {
  const [brand, setBrand] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/brand").then((r) => setBrand(r.data.brand)).catch(() => {}); }, []);
  const upload = async (field, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/admin/brand/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBrand((b) => ({ ...b, [field]: r.data.url }));
      toast.success("Uploaded — remember to save");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async () => {
    setBusy(true);
    try { await api.patch("/admin/brand", brand); toast.success("Brand settings saved — reload to see everywhere"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!brand) return <p className="cg-eyebrow animate-pulse">Loading…</p>;
  const UploadRow = ({ field, label }) => (
    <div className="flex items-center justify-between gap-4 border-b border-[var(--cg-border)] py-3" data-testid={`brand-row-${field}`}>
      <div>
        <p className="text-sm text-[#E8EAEE]">{label}</p>
        {brand[field] ? <img src={brand[field]} alt={label} className="mt-2 h-10 w-auto bg-[#222634] rounded p-1" /> : <p className="text-xs text-[#6B7280] mt-1">Not set</p>}
      </div>
      <label className="inline-flex items-center gap-2 border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-4 py-2 rounded-full hover:border-brass cg-scale-btn cursor-pointer" data-testid={`brand-upload-${field}`}>
        <Upload className="w-4 h-4" /> Upload
        <input type="file" accept="image/*,.ico,.svg" className="hidden" onChange={(e) => upload(field, e.target.files?.[0])} />
      </label>
    </div>
  );
  return (
    <div className="space-y-6" data-testid="brand-settings">
      <Card testId="brand-text">
        <h3 className="font-serif text-xl text-[#F3F4F6] mb-4">Product identity</h3>
        <div className="space-y-4">
          <Field label="Product name"><input data-testid="brand-product-name" className={input} value={brand.product_name || ""} onChange={(e) => setBrand({ ...brand, product_name: e.target.value })} /></Field>
          <Field label="Footer / copyright text"><input data-testid="brand-footer-text" className={input} value={brand.footer_text || ""} onChange={(e) => setBrand({ ...brand, footer_text: e.target.value })} /></Field>
        </div>
      </Card>
      <Card testId="brand-assets">
        <h3 className="font-serif text-xl text-[#F3F4F6] mb-2">Logos &amp; favicon</h3>
        <p className="text-sm text-[#6B7280] mb-2">Images under 5 MB. Stored durably in object storage.</p>
        <UploadRow field="logo_primary" label="Primary logo (header)" />
        <UploadRow field="logo_dark" label="Logo for dark backgrounds" />
        <UploadRow field="logo_light" label="Logo for light backgrounds" />
        <UploadRow field="logo_pdf" label="PDF / report logo" />
        <UploadRow field="favicon" label="Favicon" />
      </Card>
      <button data-testid="brand-save-button" className={btn} onClick={save} disabled={busy}>{busy ? "Saving…" : "Save brand settings"}</button>
    </div>
  );
}

function FollowupEmails() {
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const load = useCallback(() => {
    api.get("/admin/followup-templates").then((r) => setTemplates(r.data.templates)).catch(() => {});
    api.get("/admin/followup-stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (i, field, val) => setTemplates((ts) => ts.map((t, idx) => (idx === i ? { ...t, [field]: val } : t)));
  const save = async (t) => {
    try { await api.patch(`/admin/followup-templates/${t.key}`, { enabled: t.enabled, subject: t.subject, body: t.body, cta_text: t.cta_text }); toast.success("Template saved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendTest = async (key) => {
    try { const r = await api.post(`/admin/followup-templates/${key}/test`, { email: testEmail || undefined }); toast.success(`Test email sent to ${r.data.to}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const preview = (t) => (t.body || "").replace(/\[First Name\]/g, "Alex");
  const st = (k) => (stats?.per_email?.[k]) || { scheduled: 0, sent: 0, failed: 0, skipped: 0 };

  return (
    <div className="space-y-5" data-testid="followup-emails">
      <Card testId="followup-overview">
        <h3 className="font-serif text-xl text-[#F3F4F6] mb-2">Individual follow-up emails</h3>
        <p className="text-sm text-[#9CA3AF] mb-4 leading-relaxed">Automated engagement and reassessment reminders anchored to each participant's <span className="text-[#F3F4F6]">first independent</span> Authority Portrait (30 days, 3, 6 and 12 months). Organizational / campaign assessments are never enrolled, and the individual price is unchanged. Edit copy here without any code change.</p>
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4" data-testid="followup-totals">
            {[["Lifecycles", stats.totals.lifecycles], ["Scheduled", stats.totals.scheduled], ["Sent", stats.totals.sent], ["Failed", stats.totals.failed], ["Skipped", stats.totals.skipped]].map(([l, v]) => (
              <div key={l} className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg p-3">
                <p className="cg-eyebrow mb-1">{l}</p><p className="font-serif text-2xl text-[#F3F4F6]">{v}</p>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="cg-eyebrow block mb-1">Send test emails to</label>
            <input data-testid="followup-test-email" className={input} placeholder="defaults to your admin email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
          </div>
        </div>
      </Card>

      {templates.map((t, i) => (
        <Card key={t.key} testId={`followup-${t.key}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <h4 className="font-serif text-lg text-[#F3F4F6]">{t.timing_label}</h4>
              <p className="text-xs text-[#6B7280] font-mono mt-0.5">{t.key} · template {t.template_version} · CTA to {t.cta_target} · scheduled {st(t.key).scheduled} · sent {st(t.key).sent} · failed {st(t.key).failed} · skipped {st(t.key).skipped}</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#9CA3AF] cursor-pointer">
              <input type="checkbox" data-testid={`followup-enabled-${t.key}`} checked={!!t.enabled} onChange={(e) => setField(i, "enabled", e.target.checked)} className="w-4 h-4 accent-[#D9A25F]" />
              {t.enabled ? "On" : "Off"}
            </label>
          </div>
          <label className="cg-eyebrow block mb-1">Subject</label>
          <input data-testid={`followup-subject-${t.key}`} className={`${input} mb-3`} value={t.subject} onChange={(e) => setField(i, "subject", e.target.value)} />
          <label className="cg-eyebrow block mb-1">Body <span className="text-[#6B7280] normal-case">(use [First Name]; lines starting with • become bullets)</span></label>
          <textarea data-testid={`followup-body-${t.key}`} className={`${input} h-48 mb-3 font-mono text-xs`} value={t.body} onChange={(e) => setField(i, "body", e.target.value)} />
          <label className="cg-eyebrow block mb-1">CTA button text</label>
          <input data-testid={`followup-cta-${t.key}`} className={`${input} mb-3`} value={t.cta_text} onChange={(e) => setField(i, "cta_text", e.target.value)} />
          <div className="rounded-lg border border-[var(--cg-border)] bg-[#0A0B0E] p-4 mb-3" data-testid={`followup-preview-${t.key}`}>
            <p className="cg-eyebrow mb-2">Preview</p>
            <p className="text-sm text-[#E8EAEE] whitespace-pre-wrap leading-relaxed">{preview(t)}</p>
            <span className="inline-block mt-3 bg-brass text-obsidian text-sm font-medium px-4 py-1.5 rounded-full">{t.cta_text}</span>
          </div>
          <div className="flex gap-2">
            <button className={btn} data-testid={`followup-save-${t.key}`} onClick={() => save(t)}>Save</button>
            <button className={btnGhost} data-testid={`followup-test-${t.key}`} onClick={() => sendTest(t.key)}>Send test</button>
          </div>
        </Card>
      ))}
    </div>
  );
}

